import React, { FC, useEffect, useMemo, useState } from 'react';

import { Icon } from '@iconify/react';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { Trans, useTranslation } from 'react-i18next';

import { Typographies } from '~/theme/typography';

import styles from './styles.module.scss';

type Props = {
  paraphraserLevel?: string;
};

const ParaphraserLevelAnimation: FC<Props> = ({ paraphraserLevel }) => {
  const { t } = useTranslation();

  const levels = useMemo(
    () => [
      {
        key: 'minimum',
        controller: t('general:minimum', 'Minimum'),
        paraphrasedText: (
          <Trans
            t={t}
            i18nKey="modal:feature_unlock.paraphrase_level_paraphrased_text[0]"
          >
            Your brain <span>consumes</span> more energy <span>during</span>{' '}
            sleep than when you're awake
          </Trans>
        ),
      },
      {
        key: 'recommended',
        controller: t('general:recommended', 'Recommended'),
        paraphrasedText: (
          <Trans
            t={t}
            i18nKey="modal:feature_unlock.paraphrase_level_paraphrased_text[1]"
          >
            <span>The brain actually burns</span> more energy{' '}
            <span>while you’re sleeping than it does when</span> awake
          </Trans>
        ),
      },
      {
        key: 'extreme',
        controller: t('general:maximum', 'Maximum'),
        paraphrasedText: (
          <Trans
            t={t}
            i18nKey="modal:feature_unlock.paraphrase_level_paraphrased_text[2]"
          >
            <span>
              Surprisingly, your brain is more active at night, using up more
              energy in sleep than during your waking hours
            </span>
          </Trans>
        ),
      },
    ],
    [],
  );

  const initialIndex = useMemo(() => {
    const index = levels.findIndex(
      (lvl) => lvl.key === paraphraserLevel?.toLowerCase(),
    );
    return index !== -1 ? index : 0;
  }, [paraphraserLevel, levels]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    const INTERVAL_DURATION = 8000;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % levels.length);
    }, INTERVAL_DURATION);

    return (): void => clearInterval(interval);
  }, [levels.length]);

  const handleSetLevel = (index: number): void => {
    setCurrentIndex(index);
  };

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        {levels.map((level, index) => (
          <button
            key={level.controller}
            className={clsx(styles.button, {
              [styles.is_active]: currentIndex === index,
            })}
            onClick={() => handleSetLevel(index)}
          >
            {level.controller}
          </button>
        ))}
      </div>

      <div className={styles.animation}>
        <div className={styles.animation__side}>
          <Typography
            variant={Typographies.LABEL_LARGE}
            component="div"
            className={styles.animation__title}
          >
            {t('general:original', 'Original')}
          </Typography>

          <Typography
            variant={Typographies.LABEL_LARGE}
            component="div"
            className={styles.animation__text}
          >
            {t(
              'modal:feature_unlock.paraphrase_level_original_text',
              "Your brain uses more energy when you're asleep than when you're awake",
            )}
          </Typography>
        </div>

        <Icon
          icon="material-symbols:arrow-forward-rounded"
          className={styles.animation__icon}
        />

        <div className={styles.animation__side}>
          <Typography
            variant={Typographies.LABEL_LARGE}
            component="div"
            className={styles.animation__title}
          >
            {t('general:paraphrased', 'Paraphrased')}
          </Typography>

          <Typography
            variant={Typographies.LABEL_LARGE}
            component="div"
            className={styles.animation__text}
          >
            {levels[currentIndex].paraphrasedText}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default ParaphraserLevelAnimation;
